import axios from "axios";
import { authHeaders } from "./badgesCrud";

/**
 * The two read-only staff endpoints behind the Attendance and Competitions
 * tabs. Both take the staff JWT explicitly, for the reason badgesCrud's header
 * sets out at length: `setupAxios` is commented out in `src/index.js`, so the
 * only thing that authenticates these calls is the `x-auth-token` header, and
 * `axios.defaults` does not carry it until the rehydrate saga resolves — which
 * is after mount. Every caller here also holds its fetch until the token is in
 * the store, so a hard refresh or a pasted deep link cannot fire a request that
 * could only ever come back 401.
 *
 * Both endpoints are authorised by role *and* branch on the server, so a 403 is
 * an ordinary outcome for staff without the right role, not an exception — it
 * has to read as a sentence. See `describeBadgeError`'s third argument.
 */
export const ATTENDANCE_URL = "api/attendance-kiosk/attendance";
export const VISITS_URL = "api/attendance-kiosk/visits";

/**
 * Today as `YYYY-MM-DD` in the browser's own timezone, not UTC.
 *
 * This is the *viewer's* today, which is the right default for the staff
 * member standing in the branch. It can differ from the branch's own date for
 * someone viewing a distant branch near midnight; the date is right there in
 * the picker and in the URL, so it is visible and correctable rather than
 * silent. Picking a default in the branch's zone would need the branch's
 * offset, which the client is not sent.
 */
export function todayIsoDate() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * `YYYY-MM-DD`, and a date that actually exists — a junk date never reaches
 * the API.
 *
 * The round-trip through `Date.UTC` is what rejects 2026-02-31: parsing
 * "2026-02-31" rolls over to March 3 rather than returning NaN, so a plain
 * `Number.isNaN` check would wave it through. This is calendar arithmetic in
 * UTC, not formatting, and its result never reaches the screen — see the note
 * on timestamp handling at the top of `formatScanTime`.
 */
export function isIsoDate(value) {
  if (typeof value !== "string") return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * Render one of the API's three timestamps — `scanned_in_at`,
 * `checked_in_at`, `checked_out_at`.
 *
 * These arrive as branch-local wall-clock *with* the branch's UTC offset:
 *
 *     "2026-09-14T16:21:37-04:00"
 *
 * Both halves matter. The wall-clock half is already in the branch's zone, so
 * it lines up directly with `start_time` / `end_time` / `day`, which are
 * branch-local too. The offset makes the instant unambiguous.
 *
 * So the displayed time is taken from the STRING — characters 11..16 — and
 * never from a parsed `Date`.
 *
 * `new Date(value).toLocaleTimeString()` would re-render this in the *viewer's*
 * timezone. A head-office admin in Vancouver looking at a Toronto branch would
 * see every time shifted by three hours, and the bug would be invisible to
 * anyone testing in the branch's own zone — it would look perfectly correct
 * right up until the person it breaks for opens it. The 12-hour conversion
 * below is therefore done on the digits themselves; there is deliberately no
 * Date object anywhere on this path.
 */
export function formatScanTime(value) {
  if (!value) return "";
  const hhmm = value.slice(11, 16);
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  // Anything unexpected is shown raw rather than blanked — an odd-looking
  // string is debuggable, an empty cell reads as missing data.
  if (!match) return value;
  const hours = Number(match[1]);
  if (!Number.isFinite(hours) || hours > 23) return value;
  const minutes = match[2];
  const suffix = hours < 12 ? "AM" : "PM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${minutes} ${suffix}`;
}

/**
 * GET api/attendance-kiosk/attendance — the day's sessions at one branch, each
 * carrying its own roster.
 *
 * Returns `[]` rather than throwing on a well-formed response with no sessions:
 * a branch with nothing scheduled on a Sunday is an ordinary answer, and the
 * screen says so in words.
 */
export async function fetchAttendance(branchId, date, authToken) {
  const response = await axios.get(ATTENDANCE_URL, {
    ...authHeaders(authToken),
    params: { branch_id: branchId, date },
  });
  const data = response.data && response.data.data;
  const sessions = data && data.sessions;
  return Array.isArray(sessions) ? sessions.filter(Boolean) : [];
}

/** GET api/attendance-kiosk/visits — competition check-ins for one branch/day. */
export async function fetchVisits(branchId, date, authToken) {
  const response = await axios.get(VISITS_URL, {
    ...authHeaders(authToken),
    params: { branch_id: branchId, date },
  });
  const data = response.data && response.data.data;
  const visits = data && data.visits;
  return Array.isArray(visits) ? visits.filter(Boolean) : [];
}

// Every kiosk `marked_via` value starts with this; see isKioskMark below.
const KIOSK_MARK_PREFIX = "kiosk";
// The server's `marked_via` value for a typed (number or name) check-in.
export const KIOSK_TYPED_MARK = "kiosk_typed";

/**
 * Kiosk provenance values. The kiosk writes `marked_via = 'kiosk'` for a badge
 * scan and `'kiosk_typed'` when the student typed their number or name at the
 * door instead. Both are real arrivals with a real `scanned_in_at` — the
 * distinction exists for operations (many typed check-ins in a day means badges
 * are not being brought), not for this three-state model. Matched by prefix so
 * a future kiosk variant is still a scan here rather than silently becoming
 * "Marked by coach".
 */
export function isKioskMark(markedVia) {
  return typeof markedVia === "string" && markedVia.startsWith(KIOSK_MARK_PREFIX);
}

export const STUDENT_STATES = {
  SCANNED: "scanned",
  COACH_MARKED: "coach",
  UNMARKED: "unmarked",
};

/**
 * Which of the three states a roster row is in.
 *
 * This is the whole point of the Attendance tab, so it is one function with one
 * answer rather than a condition spread across the JSX:
 *
 *  - SCANNED      the student checked in at the door (badge scan or typed);
 *                 `scanned_in_at` is a real observation and is shown as a
 *                 time.
 *  - COACH_MARKED a coach marked them present (or absent) on the register.
 *                 There is no arrival time and there never was one. This is
 *                 the overwhelmingly common case — only 24 rows out of 181,398
 *                 in the live database carry a scan time — so it is rendered as
 *                 a stated fact ("Marked by coach"), never as a blank cell that
 *                 reads like a bug or a missing fetch.
 *  - UNMARKED     nobody recorded anything either way.
 *
 * A time is only ever displayed when the API sent one. Nothing here infers,
 * defaults or interpolates an arrival time from the session's start time.
 *
 * `student.makeup` is deliberately NOT consulted. A make-up student is in one
 * of these same three states like anyone else — the flag says why they are on
 * the roster, not whether they turned up. It is a qualifier, not a fourth
 * state, and the three-state contract is unchanged.
 */
export function studentState(student) {
  if (!student || student.attendance === "unmarked" || !student.attendance) {
    return STUDENT_STATES.UNMARKED;
  }
  if (isKioskMark(student.marked_via) && student.scanned_in_at) {
    return STUDENT_STATES.SCANNED;
  }
  return STUDENT_STATES.COACH_MARKED;
}

/** "Lovelace, Ada" -> sortable; blank names never crash the sort. */
export function studentName(student) {
  return [student.firstname, student.lastname]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");
}

/**
 * Minutes between check-in and check-out, or `null` when either end is missing.
 *
 * `null` is load-bearing: the caller must exclude the row from any total and
 * say that it did. See `visitTotals`.
 */
export function visitDurationMinutes(visit) {
  if (!visit || !visit.checked_in_at || !visit.checked_out_at) return null;
  // `Date` is correct *here* and wrong for display. A duration is the gap
  // between two instants, and a gap is the same number in every timezone —
  // parsing both offset-bearing ends to epoch milliseconds and subtracting is
  // exactly right, and now that the offsets are explicit it stays right across
  // a DST change mid-visit. What must never happen is formatting one of these
  // parsed values back out for the screen; see `formatScanTime`. Nothing this
  // function returns is a time of day.
  const start = new Date(visit.checked_in_at).getTime();
  const end = new Date(visit.checked_out_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.round((end - start) / 60000);
}

/**
 * Epoch milliseconds for ordering visits. Ordering by the raw string would
 * mis-sort two visits recorded either side of a DST change, whose offsets
 * differ. Like `visitDurationMinutes`, this is arithmetic and never displayed.
 */
export function visitInstant(visit) {
  const parsed = new Date((visit && visit.checked_in_at) || "").getTime();
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

/** 95 -> "1h 35m". */
export function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return "";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return `${hours}h ${rest}m`;
}

/**
 * Totals over a day's visits, with the exclusions counted rather than hidden.
 *
 * A visit with no checkout has no duration — not a zero, not "still in the
 * building until closing time". Summing it as anything at all would be a
 * fabricated number that looks perfectly reasonable on screen, which is the
 * one outcome this whole screen exists to avoid. So the sum covers only the
 * rows that can be measured, and `excluded` travels with it so the UI can
 * always state how many rows the total does not describe.
 */
export function visitTotals(visits) {
  let totalMinutes = 0;
  let counted = 0;
  let excluded = 0;
  visits.forEach((visit) => {
    const minutes = visitDurationMinutes(visit);
    if (minutes === null) excluded += 1;
    else {
      totalMinutes += minutes;
      counted += 1;
    }
  });
  return { totalMinutes, counted, excluded };
}
